/**
 * Ranked shops table (E2.1 design spec 4.6, money formatting rule 11.3,
 * link gating 11.7): ALL non-archived shops (zero-activity ones included
 * with 0.00 figures), client-sorted by gross sales desc for the selected
 * currency (dashboardHelpers.rankShopsByGross already did the sort + rank
 * assignment). A demo/training chip marks non-live shops; the sync badge
 * reflects the server-computed sync_freshness bucket with a local-time
 * last-sync tooltip. No licence-capacity column (owner ruling, spec 5.4 --
 * revoked entirely). Money columns render via MoneyText (E2.1 spec 11.3's
 * one place). The shop name links to /shops/:id ONLY when the session has
 * shops:read (spec 11.7) -- External sessions (reports:read only, no
 * shops:read) get plain text, never a dead/unauthorized link.
 *
 * Responsive per spec section 8: below sm, condenses to Shop / Gross sales /
 * Payout % / Sync (rank, payout, net hidden) via the `compact` prop the page
 * computes from useMediaQuery -- kept as a prop (not an internal media
 * query) so this table is trivially testable at either width.
 */
import { Fragment, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { RevenueShopRow } from '../../api/index.ts';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { MoneyText } from '../../components/MoneyText.tsx';
import { useDisplayCurrency } from '../../currency/CurrencyContext.tsx';
import { shopDetailPath } from '../../routing/routes.ts';
import { palette } from '../../theme.ts';
import {
  bpRateText,
  formatLocalDateTime,
  payoutRatioText,
  pickShopRevenueMinor,
  syncBadgeMeta,
  type RankedShopRow,
} from './dashboardHelpers.ts';
import { ShopsTableGhostRows } from './dashboardSkeletons.tsx';
import { ShopDailyDetail } from './ShopDailyDetail.tsx';
import { useShopDailyDetails } from './useShopDailyDetails.ts';

function shopsTableColSpan(compact: boolean, revenueColumnVisible: boolean, expandable: boolean): number {
  const expander = expandable ? 1 : 0;
  if (compact) return 4 + expander;
  return (revenueColumnVisible ? 7 : 6) + expander;
}

function SyncBadge({ freshness, lastSyncAt }: Readonly<{ freshness: RankedShopRow['sync_freshness']; lastSyncAt: string | null }>): React.JSX.Element {
  const meta = syncBadgeMeta(freshness);
  const tooltip = lastSyncAt ? `Last sync: ${formatLocalDateTime(lastSyncAt)}` : 'Never synced';
  return (
    <Tooltip title={tooltip}>
      <Chip size="small" label={meta.label} color={meta.color === 'default' ? undefined : meta.color} variant="outlined" />
    </Tooltip>
  );
}

/** "(default)" / "(custom)" per value, by comparing the row's EFFECTIVE rate
 *  against the fleet default both come from the same revenue response. */
function originTag(effective: number, fleetDefault: number | undefined): string {
  if (fleetDefault === undefined) return '';
  return effective === fleetDefault ? ' (default)' : ' (custom)';
}

/** The per-shop rates moved off the row and onto one info tooltip (owner
 *  ruling 2026-08-12) -- the column was carrying two lines of noise per row. */
function RevenueRates({
  row,
  shopName,
  defaultCommissionRateBp,
  defaultDeviceFeeMinor,
}: Readonly<{
  row: RevenueShopRow | undefined;
  shopName: string;
  defaultCommissionRateBp?: number;
  defaultDeviceFeeMinor?: number;
}>): React.JSX.Element | null {
  const { format } = useDisplayCurrency();
  if (row === undefined) return null;
  const commission = `Commission ${bpRateText(row.effective_commission_rate_bp)}${originTag(row.effective_commission_rate_bp, defaultCommissionRateBp)}`;
  const deviceFee = `Device fee ${format(row.effective_device_monthly_fee_minor, row.currency_code)}/device/month${originTag(row.effective_device_monthly_fee_minor, defaultDeviceFeeMinor)}`;
  return (
    <Tooltip title={`${commission} · ${deviceFee}`}>
      <IconButton
        size="small"
        aria-label={`Revenue rates for ${shopName}`}
        sx={{ p: 0.25, ml: 0.5, color: palette.textSecondary }}
      >
        <InfoOutlinedIcon sx={{ fontSize: '0.95rem' }} />
      </IconButton>
    </Tooltip>
  );
}

export interface ShopsTableProps {
  rows: readonly RankedShopRow[];
  currencyCode: string | null;
  compact: boolean;
  loading: boolean;
  error: string | null;
  /** 1NL revenue column (E2.1 spec 7.6) -- rendered ONLY when the session
   *  has revenue:read; the caller passes `false`/omits `revenueByShopId`
   *  entirely otherwise so an External user sees zero trace of this data. */
  showRevenue?: boolean;
  revenueByShopId?: Map<number, RevenueShopRow> | null;
  /** Fleet defaults from the same revenue response -- they decide whether a
   *  row's effective rate reads "(default)" or "(custom)". */
  defaultCommissionRateBp?: number;
  defaultDeviceFeeMinor?: number;
  /** Period the expandable daily detail queries; matches the page's controls. */
  year: number;
  month: number | null;
  reportType?: string;
  /** Daily-detail expansion is INTERNAL-STAFF CONVENIENCE ONLY, gated on
   *  reports:write so External (government) sessions see just the cumulative
   *  rows. NOT a security boundary -- reports:read still serves daily rows
   *  elsewhere by design; the server gates data, this only hides an affordance. */
  canExpandDetail?: boolean;
  /** E2.1 spec 11.7 -- the shop name becomes a /shops/:id link ONLY when the
   *  session has shops:read; plain text otherwise (never a dead link for an
   *  External session). Defaults false so a caller that forgets to pass it
   *  fails safe to plain text, not an unauthorized link. */
  shopLinkEnabled?: boolean;
}

export function ShopsTable({
  rows,
  currencyCode,
  compact,
  loading,
  error,
  showRevenue = false,
  revenueByShopId = null,
  defaultCommissionRateBp,
  defaultDeviceFeeMinor,
  year,
  month,
  reportType,
  canExpandDetail = false,
  shopLinkEnabled = false,
}: Readonly<ShopsTableProps>): React.JSX.Element {
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const { details, load } = useShopDailyDetails({ year, month, reportType });

  function toggleExpanded(shopId: number): void {
    const isOpen = expanded.has(shopId);
    // Fetch outside the updater -- load() is a side effect, and it guards its
    // own re-entry so a re-expand never refetches.
    if (!isOpen) load(shopId);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isOpen) next.delete(shopId);
      else next.add(shopId);
      return next;
    });
  }

  if (error) {
    return <LoadErrorAlert message={error} />;
  }

  const revenueColumnVisible = showRevenue && !compact;
  const colSpan = shopsTableColSpan(compact, revenueColumnVisible, canExpandDetail);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, pb: 0 }}>
        <Typography variant="h6">Shops</Typography>
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canExpandDetail && <TableCell sx={{ width: 44 }} />}
              {!compact && <TableCell>#</TableCell>}
              <TableCell>Shop</TableCell>
              <TableCell align="right">Gross sales</TableCell>
              {!compact && <TableCell align="right">Payout</TableCell>}
              <TableCell align="right">Payout ratio</TableCell>
              {!compact && <TableCell align="right">Net profit</TableCell>}
              {revenueColumnVisible && <TableCell align="right">1NL revenue</TableCell>}
              <TableCell>Sync</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Owner ruling 2026-07-15: `loading` (first load only -- the
                page keeps last rows through later reloads) renders ghost
                skeleton rows under the REAL column headers instead of the
                old one-line loading row. */}
            {loading && (
              <ShopsTableGhostRows
                compact={compact}
                revenueColumnVisible={revenueColumnVisible}
                expanderColumnVisible={canExpandDetail}
              />
            )}
            {!loading && rows.length === 0 && <EmptyRow colSpan={colSpan} message="No shops yet." />}
            {!loading &&
              rows.map((r) => (
                <Fragment key={r.shop_id}>
                <TableRow hover>
                  {canExpandDetail && (
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label={`Daily reports for ${r.shop_name}`}
                        aria-expanded={expanded.has(r.shop_id)}
                        onClick={() => toggleExpanded(r.shop_id)}
                      >
                        {expanded.has(r.shop_id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  )}
                  {!compact && <TableCell>{r.rank}</TableCell>}
                  <TableCell>
                    {shopLinkEnabled ? (
                      <Link component={RouterLink} to={shopDetailPath(r.shop_id)} sx={{ fontWeight: 600 }}>
                        {r.shop_name}
                      </Link>
                    ) : (
                      r.shop_name
                    )}
                    <Typography component="span" variant="caption" sx={{ color: palette.textSecondary, ml: 0.75 }}>
                      {r.shop_code}
                    </Typography>
                    {r.shop_type !== 'live' && (
                      <Chip
                        label={r.shop_type}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <MoneyText minor={r.row.gross_sales_minor} currencyCode={currencyCode} size="0.875rem" weight={600} />
                  </TableCell>
                  {!compact && (
                    <TableCell align="right">
                      <MoneyText minor={r.row.payout_minor} currencyCode={currencyCode} size="0.875rem" weight={600} />
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {payoutRatioText(r.row.payout_minor, r.row.gross_sales_minor, r.row.tax_total_minor)}
                  </TableCell>
                  {!compact && (
                    <TableCell align="right">
                      <MoneyText minor={r.row.profit_minor} currencyCode={currencyCode} size="0.875rem" weight={600} />
                    </TableCell>
                  )}
                  {revenueColumnVisible && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <MoneyText
                          minor={pickShopRevenueMinor(revenueByShopId ?? new Map(), r.shop_id, currencyCode ?? '')}
                          currencyCode={currencyCode}
                          size="0.875rem"
                          weight={600}
                        />
                        <RevenueRates
                          row={revenueByShopId?.get(r.shop_id)}
                          shopName={r.shop_name}
                          defaultCommissionRateBp={defaultCommissionRateBp}
                          defaultDeviceFeeMinor={defaultDeviceFeeMinor}
                        />
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <SyncBadge freshness={r.sync_freshness} lastSyncAt={r.last_sync_at} />
                  </TableCell>
                </TableRow>
                {canExpandDetail && expanded.has(r.shop_id) && (
                  <TableRow>
                    <TableCell colSpan={colSpan} sx={{ py: 0, bgcolor: palette.quietHover }}>
                      <ShopDailyDetail shopId={r.shop_id} detail={details.get(r.shop_id)} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default ShopsTable;
